import { textUtils } from '@sato/components';

import {
  AuthMethod,
  IdentificationType,
} from '@components/FTNAuthentication.utils';
import { LanguageKey } from '@models/Language';
import { createAsyncTask } from '@stores/taskStore';

import { authClient, publicClient } from './client.api';

const SIGNUP_API_URL = process.env.SIGNUP_API_URL ?? '';

export const SIGNUP_API_URLS = {
  SIGNIN: `${SIGNUP_API_URL}/v1/auth/signin`,
  SIGNOUT: `${SIGNUP_API_URL}/v1/auth/signout`,
  SIGNUP_QUICKLINK_REGISTER: `${SIGNUP_API_URL}/v3/signup/register/quick`,
  SIGNUP: `${SIGNUP_API_URL}/v3/signup/register`,
  VALIDATE_SIGNUP_TOKEN: `${SIGNUP_API_URL}/v3/signup/validate`,
  VALIDATE_QUICKLINK_TOKEN: `${SIGNUP_API_URL}/v3/signup/validate/quick`,
  VALIDATE_VERIFICATION_CODE: `${SIGNUP_API_URL}/v3/signup/confirm`,
  FORGOT_PASSWORD: `${SIGNUP_API_URL}/v1/auth/resetpassword`,
  CONFIRM_PASSWORD: `${SIGNUP_API_URL}/v1/auth/confirmpassword`,
  FTN_AUTHENTICATE: process.env.TUPAS_REQUEST_REDIRECT_URL ?? '',
  REFRESH_TOKEN: `${SIGNUP_API_URL}/v1/auth/signinrefresh`,
  CHANGE_PASSWORD: `${SIGNUP_API_URL}/v1/auth/changepassword`,
  SEND_VERIFICATION_EMAIL: `${SIGNUP_API_URL}/auth/sendemailverification`,
  VERIFY_EMAIL: `${SIGNUP_API_URL}/v1/auth/verifyemaill`,
  RESEND_VERIFICATION_CODE: `${SIGNUP_API_URL}/v3/signup/resendconfirmationcode`,
} as const;

// TODO: is this relevant
const headers = {
  'Auth-Type': 'awscognito',
};

export const signupApi = {
  confirmPassword: createAsyncTask(
    'confirm-password',
    (data: {
      newPassword: string;
      confirmationCode: string;
      username: string;
    }) => publicClient.post(SIGNUP_API_URLS.CONFIRM_PASSWORD, data)
  ),

  verifyEmail: ({ code, email }: { code: string; email: string }) =>
    publicClient.post<void>(
      SIGNUP_API_URLS.VALIDATE_VERIFICATION_CODE,
      {
        confirmationCode: code,
        username: email,
      },
      { headers }
    ),

  resendConfirmationCode: createAsyncTask(
    'resend-confirmation-code',
    (email: string) =>
      publicClient.post<void>(SIGNUP_API_URLS.RESEND_VERIFICATION_CODE, {
        username: email,
      })
  ),

  register: createAsyncTask(
    'register',
    (payload: {
      username: string;
      password: string;
      token: string;
      language: LanguageKey;
    }) => publicClient.post<void>(SIGNUP_API_URLS.SIGNUP, payload)
  ),

  /** API utility for obtaining FTN authentication URL */
  authenticate: (payload: {
    frontRedirectUri: string;
    errorRedirectUrl: string;
    language: LanguageKey;
    identificationType: IdentificationType;
    method: AuthMethod;
  }) =>
    publicClient.post<{ redirectUri: string }>(
      SIGNUP_API_URLS.FTN_AUTHENTICATE,
      payload,
      { headers }
    ),

  validateToken: (token: string) =>
    publicClient.post<{
      firstNames: string;
      lastName: string;
    }>(SIGNUP_API_URLS.VALIDATE_SIGNUP_TOKEN, { token }, { headers }),

  quick: {
    validateToken: (token: string) =>
      publicClient.post<{
        email: string;
        name: string;
      }>(SIGNUP_API_URLS.VALIDATE_QUICKLINK_TOKEN, { token }, { headers }),

    register: (token: string, password: string) =>
      publicClient.post<void>(
        SIGNUP_API_URLS.SIGNUP_QUICKLINK_REGISTER,
        { invitationToken: token, password },
        { headers }
      ),
  },
};

export const loginApi = {
  forgotPassword: (email: string) =>
    publicClient.post<void>(SIGNUP_API_URLS.FORGOT_PASSWORD, {
      username: textUtils.removeWhitespaces(email.toLowerCase()),
    }),

  changePassword: createAsyncTask(
    'change-password',
    (payload: { newPassword: string; oldPassword: string }) =>
      authClient.post(SIGNUP_API_URLS.CHANGE_PASSWORD, payload)
  ),
};
